// Top-level crash handler. Catches uncaught errors from the frame loop,
// promise rejections, and any window.onerror event. Logs a rolling 5
// entries to localStorage (so a returning player can hand over a crash
// trace) and surfaces a modal that lets them reload — instead of staring
// at a frozen scene with the dev tools closed.
//
// Idempotent: install() returns the same handler if called more than
// once. Modal mounts lazily on first crash; further crashes append to
// the trace list.
//
// This is jam-juror insurance: a single throw in main.ts (3000 lines)
// would otherwise white-screen the page.

const STORAGE_KEY = 'smp.crashLog';
const MAX_ENTRIES = 5;

type CrashEntry = {
  ts: number;
  context: string;
  message: string;
  stack: string;
  url: string;
};

let installed = false;
let modalMounted = false;
let modalRoot: HTMLDivElement | null = null;
let traceList: HTMLDivElement | null = null;

export type CrashReporter = (err: unknown, context: string) => void;

export function installCrashHandler(): CrashReporter {
  if (installed) return reportCrash;
  installed = true;

  addEventListener('error', (e) => {
    reportCrash(e.error ?? new Error(e.message), 'window.error');
  });
  addEventListener('unhandledrejection', (e) => {
    reportCrash(e.reason ?? new Error('unhandled rejection'), 'unhandledrejection');
  });

  return reportCrash;
}

export function reportCrash(err: unknown, context: string): void {
  const entry: CrashEntry = {
    ts: Date.now(),
    context,
    message: errorMessage(err),
    stack: errorStack(err),
    url: typeof location !== 'undefined' ? location.href : 'n/a',
  };
  console.error('[crash]', context, entry.message, entry.stack);
  pushToLog(entry);
  showModal(entry);
}

function pushToLog(entry: CrashEntry): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const log: CrashEntry[] = raw ? JSON.parse(raw) : [];
    log.push(entry);
    while (log.length > MAX_ENTRIES) log.shift();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(log));
  } catch { /* localStorage may be denied — never block the modal */ }
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  try { return JSON.stringify(err); } catch { return String(err); }
}

function errorStack(err: unknown): string {
  if (err instanceof Error && err.stack) return err.stack.slice(0, 1200);
  return '(no stack)';
}

function showModal(entry: CrashEntry): void {
  if (!modalMounted) mountModal();
  if (!modalRoot || !traceList) return;
  modalRoot.style.display = 'flex';
  // Append the latest trace to the top of the list (newest first).
  const row = document.createElement('div');
  row.style.cssText = `
    margin-bottom: 10px; padding: 8px 10px;
    background: #0f172a; border: 1px solid #475569; border-radius: 6px;
    font: 500 10px ui-monospace, monospace; color: #cbd5e1;
    white-space: pre-wrap; word-break: break-word; max-height: 160px; overflow-y: auto;
  `;
  row.textContent = `[${entry.context}] ${entry.message}\n${entry.stack}`;
  traceList.prepend(row);
}

function mountModal(): void {
  if (modalMounted) return;
  modalMounted = true;
  const root = document.createElement('div');
  root.className = 'ui interactive';
  root.style.cssText = `
    position: fixed; inset: 0; z-index: 99999;
    display: none;
    background: rgba(2,6,23,.92);
    align-items: center; justify-content: center;
    backdrop-filter: blur(4px);
    font: 600 12px ui-monospace, monospace; color: #e5e7eb;
  `;
  const card = document.createElement('div');
  card.style.cssText = `
    background: #1e293b; border: 2px solid #ef4444; border-radius: 14px;
    padding: 22px 24px; width: min(560px, 92vw); max-height: 84vh; overflow-y: auto;
    box-shadow: 0 24px 80px rgba(239,68,68,.35);
  `;
  card.innerHTML = `
    <div style="font: 900 18px ui-monospace; color:#ef4444; letter-spacing: 2px; margin-bottom: 4px;">
      🛑 GAME ERROR
    </div>
    <div style="color:#cbd5e1; font-size: 11px; margin-bottom: 14px;">
      The game hit an unexpected error. Reload to recover. If this keeps happening,
      copy the trace below and report it.
    </div>
    <div data-trace style="margin-bottom: 14px;"></div>
    <div style="display: flex; gap: 8px;">
      <button data-act="reload" style="
        flex: 1; padding: 10px 14px;
        background: #ef4444; border: 0; border-radius: 6px;
        color: #fff; font: 800 12px ui-monospace; letter-spacing: 1px;
        cursor: pointer;
      ">RELOAD</button>
      <button data-act="copy" style="
        flex: 0 0 auto; padding: 10px 14px;
        background: #334155; border: 0; border-radius: 6px;
        color: #e5e7eb; font: 700 11px ui-monospace;
        cursor: pointer;
      ">COPY TRACE</button>
      <button data-act="dismiss" style="
        flex: 0 0 auto; padding: 10px 14px;
        background: transparent; border: 1px solid #475569; border-radius: 6px;
        color: #94a3b8; font: 700 11px ui-monospace;
        cursor: pointer;
      ">DISMISS</button>
    </div>
  `;
  root.appendChild(card);
  document.body.appendChild(root);
  modalRoot = root;
  traceList = card.querySelector('[data-trace]') as HTMLDivElement;

  card.querySelector('[data-act="reload"]')?.addEventListener('click', () => {
    location.reload();
  });
  card.querySelector('[data-act="copy"]')?.addEventListener('click', () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY) ?? '[]';
      navigator.clipboard?.writeText(raw);
    } catch { /* clipboard may be denied */ }
  });
  card.querySelector('[data-act="dismiss"]')?.addEventListener('click', () => {
    if (modalRoot) modalRoot.style.display = 'none';
  });
}
